import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '../api/client';
import * as authApi from '../api/auth';
import * as profileApi from '../api/profile';
import type { UserProfile } from '../api/profile';
import { purgeAnonymousTelemetryIfStored } from '../features/telemetry/purge';
import { withTimeout } from '../shared/lib/withTimeout';

const INITIAL_AUTH_TIMEOUT_MS = 3000;

interface AuthState {
  user: authApi.User | null;
  profile: UserProfile | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthState & {
  login: (email: string, password: string) => Promise<void>;
  register: (params: { email: string; password: string; name: string; phone?: string; role?: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
} | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<authApi.User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshProfile = useCallback(async () => {
    const p = await profileApi.getProfile();
    setProfile(p);
  }, []);

  const loadSession = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        try {
          const data = await withTimeout(
            authApi.refresh(),
            INITIAL_AUTH_TIMEOUT_MS,
            'Auth refresh timeout'
          );
          if (data?.user) {
            setUser(data.user);
            authApi.setAccessToken?.(data.accessToken);
            await refreshProfile();
            await purgeAnonymousTelemetryIfStored();
          }
        } catch (err) {
          console.warn('Auth bootstrap refresh skipped for public view', err);
          setUser(null);
          setProfile(null);
        }
        return;
      }
      const p = await withTimeout(
        profileApi.getProfile(),
        INITIAL_AUTH_TIMEOUT_MS,
        'Profile bootstrap timeout'
      );
      if (p) {
        setUser({ id: p.userId, email: p.email, name: p.name, createdAt: p.createdAt, updatedAt: p.updatedAt });
        setProfile(p);
        await purgeAnonymousTelemetryIfStored();
      } else {
        setUser(null);
        setProfile(null);
      }
    } catch (err) {
      console.warn('Auth bootstrap failed; continuing as guest', err);
      setUser(null);
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, [refreshProfile]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await authApi.login(email, password);
    setUser(data.user);
    authApi.setAccessToken(data.accessToken);
    await refreshProfile();
    await purgeAnonymousTelemetryIfStored();
  }, [refreshProfile]);

  const register = useCallback(async (params: {
    email: string;
    password: string;
    name: string;
    phone?: string;
    role?: string;
  }) => {
    const data = await authApi.register(params);
    setUser(data.user);
    authApi.setAccessToken(data.accessToken);
    await refreshProfile();
    await purgeAnonymousTelemetryIfStored();
  }, [refreshProfile]);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setProfile(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        isLoading,
        login,
        register,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function getAuthToken(): string | null {
  return getAccessToken();
}

